import type { MixSheetRecipe, SizeId, SizeOption } from "./types";

// Single code-level contract for the currently supported sticker size ids and
// the corresponding Shopify option values.
export const STICKER_SIZE_OPTION_VALUES: Record<SizeId, string> = {
  small: "Small",
  medium: "Medium",
  large: "Large",
};

export const STICKER_SIZE_IDS: SizeId[] = Object.keys(STICKER_SIZE_OPTION_VALUES);

export const DEFAULT_STICKER_SIZE_ID: SizeId | undefined = STICKER_SIZE_IDS[0];

/**
 * The size id behind "Standard" (formerly Medium, spec #107/#108): the one
 * single size the selector offers, the ladder's single-size variant and the
 * phone sticker's size. Named so call sites say "Standard", not `"medium"`.
 */
export const STANDARD_SIZE_ID: SizeId = "medium";

// Single sizes offered as standalone choices in the size selector ("Standard").
// Small and Large stay in the size contract and the Shopify catalog because
// Mix recipes reference them and old orders, edit and reorder links
// reconstruct from them — they are only hidden from the selector.
const OFFERED_SINGLE_SIZE_IDS: ReadonlySet<SizeId> = new Set<SizeId>([STANDARD_SIZE_ID]);

export function isOfferedSingleSizeId(sizeId: SizeId): boolean {
  return OFFERED_SINGLE_SIZE_IDS.has(sizeId);
}

// Ordering index for slug stability — two recipes with items in different JSON
// order collapse to the same slug.
const STICKER_SIZE_ORDER: Record<string, number> = Object.fromEntries(
  STICKER_SIZE_IDS.map((sizeId, index) => [sizeId, index]),
);

// First-character guard: if a future SizeId shares a first char with an
// existing one (e.g. "small" / "super"), the slug scheme silently collides.
// Fail loud at module load so the contract is visible in test output, not at
// the URL boundary.
const STICKER_SIZE_SLUG_CHARS: Record<string, string> = (() => {
  const seen = new Map<string, string>();
  for (const sizeId of STICKER_SIZE_IDS) {
    const char = sizeId[0];
    if (!char) {
      throw new Error(`SizeId "${sizeId}" is empty and cannot produce a mix slug character.`);
    }
    const previous = seen.get(char);
    if (previous) {
      throw new Error(
        `SizeIds "${previous}" and "${sizeId}" share the mix-slug first character "${char}". Extend STICKER_SIZE_IDS with a collision-free scheme before adding a second size with the same initial.`,
      );
    }
    seen.set(char, sizeId);
  }
  return Object.fromEntries(STICKER_SIZE_IDS.map((sizeId) => [sizeId, sizeId[0]]));
})();

/**
 * Deterministic slug for a mix-sheet recipe, derived from its items.
 *
 * Items are sorted by `STICKER_SIZE_IDS` order and joined as
 * `${count}${sizeId[0]}` so `{items:[{sizeId:"medium",count:2},{sizeId:"small",count:4}]}`
 * and `{items:[{sizeId:"small",count:4},{sizeId:"medium",count:2}]}` produce
 * the same slug (`"4s-2m"`). Framework-free so it can run in loaders and
 * server parsing without pulling in React or nuqs.
 */
export function buildMixSlug(recipe: MixSheetRecipe): string {
  const orderedItems = [...recipe.items].sort((a, b) => {
    const orderA = STICKER_SIZE_ORDER[a.sizeId];
    const orderB = STICKER_SIZE_ORDER[b.sizeId];
    if (orderA === undefined || orderB === undefined) {
      // Parser contract upstream rejects unknown sizeIds, but guard here so a
      // bad caller can't produce a nondeterministic slug.
      throw new Error(
        `Mix recipe contains unknown sizeId "${orderA === undefined ? a.sizeId : b.sizeId}".`,
      );
    }
    return orderA - orderB;
  });

  return orderedItems
    .map((item) => `${item.count}${STICKER_SIZE_SLUG_CHARS[item.sizeId]}`)
    .join("-");
}

/**
 * Last-resort fallback when Shopify's variant catalog is unavailable. Per
 * DEC-0018, Shopify's `custom.label_width_mm` / `custom.label_height_mm`
 * metafields are the runtime source of truth — production code paths
 * (`createShopifyBackedSizeOption`) always overwrite these values with the
 * Shopify-resolved ones. The fallback exists so domain helpers like
 * `getSizeById` can still hand back a `SizeOption` skeleton when called in
 * isolation (e.g. preview pixel sizing during tests). Keep these in sync
 * with the test fixture in
 * `src/integrations/shopify/__tests__/sticker-size-fixtures.ts`.
 */
const FALLBACK_SIZE_DIMENSIONS_BY_ID: Record<string, SizeOption["dimensions"]> = {
  small: { widthMm: 16, heightMm: 8 },
  medium: { widthMm: 30, heightMm: 13 },
  large: { widthMm: 48, heightMm: 26 },
};

export function isSupportedStickerSizeId(value: string): value is SizeId {
  return value in STICKER_SIZE_OPTION_VALUES;
}

export function getStickerSizeOptionValue(sizeId: SizeId): string | undefined {
  return STICKER_SIZE_OPTION_VALUES[sizeId];
}

export function getFallbackStickerSizeDimensions(
  sizeId: SizeId,
): SizeOption["dimensions"] | undefined {
  return FALLBACK_SIZE_DIMENSIONS_BY_ID[sizeId];
}

export function createFallbackStickerSizeOption(sizeId: SizeId): SizeOption | undefined {
  const label = getStickerSizeOptionValue(sizeId);
  const dimensions = getFallbackStickerSizeDimensions(sizeId);

  if (!label || !dimensions) {
    return undefined;
  }

  return {
    id: sizeId,
    label,
    dimensions,
  };
}
